import { useState, useEffect } from "react";
import Editor from "@monaco-editor/react";
import { FileCode, Save, Loader2, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const langFor = (path) => {
  if (path.endsWith(".html")) return "html";
  if (path.endsWith(".css")) return "css";
  if (path.endsWith(".js") || path.endsWith(".jsx")) return "javascript";
  if (path.endsWith(".ts") || path.endsWith(".tsx")) return "typescript";
  if (path.endsWith(".json")) return "json";
  if (path.endsWith(".md")) return "markdown";
  return "plaintext";
};

export default function CodePanel({ projectId, files, activePath, setActivePath, onFileSaved }) {
  const active = files.find((f) => f.path === activePath) || files[0];
  const [draft, setDraft] = useState(active?.content || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(active?.content || ""); }, [active?.path, active?.content]);

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/files`, { path: active.path, content: draft });
      toast.success(`Saved ${active.path}`);
      onFileSaved?.(active.path, draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#1e1e1e]">
      <div className="h-11 shrink-0 border-b border-border flex items-center justify-between px-4 bg-background">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Code2 className="w-4 h-4" />
          <span className="font-mono text-xs uppercase tracking-wider">Code</span>
        </div>
        <Button data-testid="save-file-btn" size="sm" variant="outline" onClick={save} disabled={saving || !active}
          className="h-7 border-border text-xs">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Save</>}
        </Button>
      </div>
      <div className="flex flex-1 min-h-0">
        {/* file tree */}
        <div className="w-40 shrink-0 border-r border-border bg-background overflow-y-auto py-2">
          {files.length === 0 ? (
            <p className="px-3 text-xs text-zinc-600 font-mono">no files</p>
          ) : (
            files.map((f) => (
              <button key={f.path} data-testid={`file-${f.path.replace(/[^a-z0-9]/gi, "-")}`}
                onClick={() => setActivePath(f.path)}
                className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-left text-xs font-mono truncate transition-colors ${
                  active?.path === f.path ? "bg-secondary text-white" : "text-muted-foreground hover:text-white hover:bg-secondary/50"
                }`}>
                <FileCode className="w-3 h-3 shrink-0" /> {f.path}
              </button>
            ))
          )}
        </div>
        {/* editor */}
        <div className="flex-1 min-w-0">
          {active ? (
            <Editor
              key={active.path}
              height="100%"
              theme="vs-dark"
              language={langFor(active.path)}
              value={draft}
              onChange={(v) => setDraft(v ?? "")}
              options={{ fontSize: 13, fontFamily: "JetBrains Mono, monospace", minimap: { enabled: false },
                scrollBeyondLastLine: false, padding: { top: 12 }, automaticLayout: true }}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <p className="font-mono text-sm text-zinc-600">Generated files appear here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
